import { Box, BoxProps } from '@mui/material'
import { BRAND_MARKS } from 'src/design-system/brand.generated'

// ----------------------------------------------------------------------
// Google's G. A third-party trademark in its owner's four colours, and not an
// icon: it cannot go through `Iconify`, which strokes a `fill: none` path, and
// it cannot take the theme's ink, because in one colour it is no longer
// Google's mark. Its colours are kept as drawn (`design-system/brand/fixed`).
//
// Used where the thing named is Google: the Continue with Google button, a
// linked Google account in Settings, and a Google sign-in on the Managers
// screen.
// ----------------------------------------------------------------------

export interface GoogleMarkProps
  extends Omit<
    BoxProps,
    'children' | 'width' | 'height' | 'dangerouslySetInnerHTML'
  > {
  /** Rendered size in px. The mark is square. */
  size?: number
  /**
   * What the mark says, when nothing beside it already says it. Left off it
   * is decorative and hidden, as it is inside a button reading Continue with
   * Google.
   */
  title?: string
}

/** Beside a button label, the size Google sets its G at. */
export const GOOGLE_MARK_INLINE = 18

export default function GoogleMark({
  size = GOOGLE_MARK_INLINE,
  title,
  sx,
  ...other
}: GoogleMarkProps & {
  width?: never
  height?: never
  dangerouslySetInnerHTML?: never
}) {
  const mark = BRAND_MARKS['google-g']

  return (
    <Box
      component="svg"
      viewBox={mark.viewBox}
      width={size}
      height={size}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      // A brand mark fills its box edge to edge, where a line glyph stands in
      // from it, so a button gives it more room before the label.
      data-brand-mark=""
      sx={[
        { display: 'block', flex: '0 0 auto' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      dangerouslySetInnerHTML={{ __html: mark.body }}
      {...other}
    />
  )
}
